import { CompanyNode, LayoutDirection, PercentageDisplayMode } from '../types';

// Extended type to include node positions
export interface SavedProject {
  version: string;
  savedAt: string;
  companyNodes: CompanyNode[];
  nodePositions: Record<string, { x: number; y: number }>;
  layout: LayoutDirection;
  maxDepth: number | 'all';
  showCountryFlags: boolean;
  percentageDisplayMode?: PercentageDisplayMode;
}

const PROJECT_VERSION = '1.0.0';
const LOCALSTORAGE_KEY = 'hvg-org-chart-autosave';

/**
 * Save project to localStorage (for auto-save)
 */
export function saveToLocalStorage(project: SavedProject): void {
  try {
    const data = JSON.stringify({
      ...project,
      savedAt: new Date().toISOString(),
      version: PROJECT_VERSION
    });
    localStorage.setItem(LOCALSTORAGE_KEY, data);
  } catch (error) {
    console.warn('Failed to save to localStorage:', error);
    // localStorage might be full or disabled
  }
}

/**
 * Load project from localStorage
 */
export function loadFromLocalStorage(): SavedProject | null {
  try {
    const data = localStorage.getItem(LOCALSTORAGE_KEY);
    if (!data) return null;
    
    const project = JSON.parse(data) as SavedProject;
    // Validate version compatibility (add version migration logic if needed)
    return project;
  } catch (error) {
    console.warn('Failed to load from localStorage:', error);
    return null;
  }
}

/**
 * Clear auto-saved project from localStorage
 */
export function clearLocalStorage(): void {
  try {
    localStorage.removeItem(LOCALSTORAGE_KEY);
  } catch (error) {
    console.warn('Failed to clear localStorage:', error);
  }
}

/**
 * Save project as JSON file (download)
 */
export function saveProjectToFile(project: SavedProject): void {
  try {
    const data = JSON.stringify(project, null, 2);
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `org-chart-project-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    throw new Error(`Failed to save project file: ${error}`);
  }
}

/**
 * Load project from JSON file
 */
export function loadProjectFromFile(file: File): Promise<SavedProject> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (event) => {
      try {
        const text = event.target?.result as string;
        const project = JSON.parse(text) as SavedProject;
        
        // Validate project structure
        if (!project.companyNodes || !Array.isArray(project.companyNodes)) {
          throw new Error('Invalid project file: missing or invalid companyNodes');
        }
        
        resolve({
          ...project,
          nodePositions: project.nodePositions || {},
          layout: project.layout || 'TB',
          maxDepth: project.maxDepth ?? 'all',
          showCountryFlags: project.showCountryFlags ?? true,
          percentageDisplayMode: project.percentageDisplayMode ?? 'card',
        });
      } catch (error) {
        reject(new Error(`Failed to parse project file: ${error}`));
      }
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
    
    reader.readAsText(file);
  });
}

